import { Box, Button, FormHelperText, Typography } from '@material-ui/core'
import Checkbox from '@material-ui/core/Checkbox'
import Chip from '@material-ui/core/Chip'
import CircularProgress from '@material-ui/core/CircularProgress'
import FormControl from '@material-ui/core/FormControl'
import { PopoverProps } from '@material-ui/core/Popover'
import { withStyles, WithStyles } from '@material-ui/core/styles'
import createStyles from '@material-ui/core/styles/createStyles'
import { Theme } from '@material-ui/core/styles/createTheme'
import TextField from '@material-ui/core/TextField'
import Autocomplete, { AutocompleteGetTagProps } from '@material-ui/lab/Autocomplete'
import classNames from 'classnames'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { AreaLabelResponse } from 'src/api/gen'
import { sortObjectsByLabel } from 'src/commons/helpers'

import {
  CustomBlankCheckboxIcon,
  CustomCheckboxIcon,
  CustomDeleteCheckboxIcon,
} from '../SidePanel/Components/CheckboxesSection'

const styles = (theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      maxWidth: 300,
    },
    chip: {
      margin: theme.spacing(1 / 4),
      fontSize: '13px',
      borderRadius: '10px',
      background: theme.palette.primary.main,
      border: 'none',
      color: 'white',
      overflow: 'hidden',
    },
    chipDeleteIcon: {
      color: 'white',
      width: '20px',
    },
    formControl: {
      width: '100%',
    },
    select: {
      display: 'flex',
      flexWrap: 'wrap',
      // padding/minHeight hack so that the multi select will have the same height as the normal select
      // whether it is empty or not inside, if it does not have too many elements
      paddingTop: '5px',
      paddingBottom: '5px',
      minHeight: '34px',
    },
    selectOneLine: {
      display: 'flex',
      flexWrap: 'wrap',
      height: '45px',
      overflowY: 'visible',
      // padding/minHeight hack so that the multi select will have the same height as the normal select
      // whether it is empty or not inside, if it does not have too many elements
      paddingTop: '5px',
      paddingBottom: '5px',
    },
    buttons: {
      margin: '8px 16px 8px 0',
    },
    listItemText: {
      padding: 0,
    },
    listItem: {
      paddingTop: '6px',
      paddingBottom: '6px',
    },
    disabledArrow: {
      display: 'none',
    },
    menu: {
      maxHeight: 30,
      width: '100%',
      height: '100%',
      padding: 0,
      margin: 0,
    },
  })

interface IMultiSelectChipsProps extends WithStyles<typeof styles> {
  className?: string
  classes: any
  data?: AreaLabelResponse[]
  disabled?: boolean
  error?: boolean
  helperText?: React.ReactNode
  id?: string
  inputProps?: { [key: string]: string }
  loading?: boolean
  onMultiChange: (values: string[]) => void
  oneLineSelect?: boolean
  popoverProps?: Partial<PopoverProps>
  removeItem?: boolean
  value: string[]
  withSelectAll?: boolean
}

const MultiSelectChips = (props: IMultiSelectChipsProps) => {
  const {
    id,
    classes,
    className,
    onMultiChange,
    data = [],
    value,
    error = false,
    removeItem = false,
    withSelectAll = false,
    helperText,
    disabled = false,
    inputProps,
    loading = false,
  } = props
  const { t } = useTranslation()

  return (
    <div className={classNames(classes.root, className)} id={id}>
      <FormControl className={classes.formControl} error={error} variant="outlined">
        {withSelectAll && (
          <div>
            <Button
              disabled={disabled}
              className={classes.buttons}
              variant="outlined"
              onClick={() => onMultiChange(data.map((datum) => datum.id))}
            >
              {t('Select all')}
            </Button>
            <Button
              disabled={disabled}
              className={classes.buttons}
              variant="outlined"
              onClick={() => onMultiChange([])}
            >
              {t('Deselect all')}
            </Button>
          </div>
        )}

        <Autocomplete
          multiple
          value={data.filter((d) => value.indexOf(d.id) > -1).sort(sortObjectsByLabel)}
          options={data.sort(sortObjectsByLabel)}
          disableCloseOnSelect
          disabled={disabled}
          loading={loading}
          onChange={(_event, values) => {
            onMultiChange(values?.map((v) => v.id) ?? [])
          }}
          forcePopupIcon={disabled ? false : 'auto'}
          getOptionLabel={(option) => option.label}
          renderTags={(tagValue, _getTagProps: AutocompleteGetTagProps) =>
            tagValue.map((option, index) => {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              const { onDelete, ...tagProps } = _getTagProps({ index })

              return (
                <Chip
                  {...tagProps}
                  key={option.id}
                  label={option.label}
                  classes={{
                    root: classes.chip,
                    deleteIcon: classes.chipDeleteIcon,
                  }}
                  onDelete={disabled ? undefined : onDelete}
                />
              )
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              inputProps={{
                ...params.inputProps,
                ...inputProps,
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading && <CircularProgress color="secondary" size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              variant="outlined"
              className={classes.input}
              margin="dense"
              error={error}
            />
          )}
          renderOption={(d) => (
            <Box display="flex" alignItems="center">
              <Checkbox
                icon={<CustomBlankCheckboxIcon />}
                checkedIcon={removeItem ? <CustomDeleteCheckboxIcon /> : <CustomCheckboxIcon />}
                color="primary"
                checked={value.indexOf(d.id) > -1}
              />
              <Typography className={classes.listItemText}> {d.label} </Typography>
            </Box>
          )}
        />
        {helperText && (
          <FormHelperText className={classes.helper} error={error}>
            {helperText}
          </FormHelperText>
        )}
      </FormControl>
    </div>
  )
}

export default withStyles(styles)(MultiSelectChips)
